import React, {useEffect, useMemo, useRef, useState, useCallback} from 'react';
import {connect} from 'react-redux';
import classNames from 'classnames';

import {updateObject} from '../../utils/reducerUtils';
import strings from '../../utils/strings';
import AudioPlayer from '../../components/AudioPlayer';

import previewVideoNo from './assets/tutorial.mp4';
import previewVideoNn from './assets/tutorial.mp4';
import previewVideoSe from './assets/SE_Ordkjede.mp4';
import previewVideoDe from './assets/DE_Ordkjede.mp4';

import {VARIATION} from '../../constants';

const WHITE_SPACE_LIMIT = 3;
const whiteSpaceRegex = /\s+/g;

const stripWhiteSpace = (str = '') => str.replace(whiteSpaceRegex, '');
const normalizeWhiteSpace = (str = '') => str.trim().replace(whiteSpaceRegex, ' ');
const getNumberOfWhiteSpaces = (str = '') => {
	const matches = str.match(whiteSpaceRegex);
	return matches ? matches.length : 0;
};

/**
 * Auto-detect a "solved example" row:
 * - text already contains 3 whitespace groups
 * - and text/answer match (ignoring whitespace)
 * Also supports explicit `locked: true` in the dataset.
 */
const isLockedAssignment = (a) => {
	if (a.locked) return true;
	const textHas3Spaces = getNumberOfWhiteSpaces(a.text) === WHITE_SPACE_LIMIT;
	const sameIgnoringSpaces = stripWhiteSpace(a.text) === stripWhiteSpace(a.answer);
	return textHas3Spaces && sameIgnoringSpaces;
};

const Test = ({onNextStep}) => {
	const variation = VARIATION;
	const language = strings.getLanguage();

	const previewVideoObj = useMemo(
		() => ({
			no: previewVideoNo,
			nn: previewVideoNn,
			se: previewVideoSe,
			de: previewVideoDe,
		}),
		[]
	);

	console.log('variation ', variation);
	console.log('language ', language);

	const videoRef = useRef(null);
	const audioRefs = useMemo(() => ({}), []);

	const startTheTestSound = useMemo(
		() =>
			require(`../../assets/sounds/${variation}/se/2 7 Ordkjedetest-Starter-testen.mp3`),
		[variation, language]
	);

	const introAudio = useMemo(
		() =>
			require(`../../assets/sounds/${variation}/se/2 2 Ordkjedetest-her-ser-du-4-ovingsoppgaver.mp3`),
		[variation, language]
	);

	const correctAnswerSound = useMemo(
		() =>
			require(`../../assets/sounds/${variation}/se/2 5 Ordkjedetest-Riktig.mp3`),
		[variation, language]
	);

	const assignments = useMemo(
		() => require(`../../assets/testdata/${variation}/se/wordchain-tutorial`).default,
		['adult', 'se']
	);

	const videoSrc = useMemo(() => previewVideoObj[language], [previewVideoObj, language]);

	const initialResults = useMemo(() => {
		const results = {};
		for (const a of assignments) results[a.id] = a.text;
		return results;
	}, [assignments]);

	const [results, setResults] = useState(initialResults);

	// reset if language/testdata changes
	useEffect(() => {
		setResults(initialResults);
		setPhase('idle');
	}, [initialResults]);

	const lockedMap = useMemo(() => {
		const map = {};
		for (const a of assignments) map[a.id] = isLockedAssignment(a);
		return map;
	}, [assignments]);

	const editableAssignments = useMemo(
		() => assignments.filter((a) => !lockedMap[a.id]),
		[assignments, lockedMap]
	);

	const firstEditableId = useMemo(
		() => (editableAssignments[0] ? editableAssignments[0].id : null),
		[editableAssignments]
	);

	const allRequiredCompleted = useMemo(() => {
		return editableAssignments.every(
			(a) => getNumberOfWhiteSpaces(results[a.id]) === WHITE_SPACE_LIMIT
		);
	}, [editableAssignments, results]);

	const allRequiredCorrect = useMemo(() => {
		return editableAssignments.every((a) => {
			const value = results[a.id];
			return (
				getNumberOfWhiteSpaces(value) === WHITE_SPACE_LIMIT &&
				value === a.answer
			);
		});
	}, [editableAssignments, results]);

	/**
	 * Phases:
	 * - idle: not ready
	 * - playStartSound: play "starter testen"
	 * - ready: show start button
	 */
	const [phase, setPhase] = useState('idle');

	// For non-SE: as soon as everything is correct -> play start sound.
	useEffect(() => {
		if (!allRequiredCompleted) {
			setPhase('idle');
			return;
		}
		if (language !== 'se') {
			setPhase('playStartSound');
		}
	}, [allRequiredCompleted, language]);

	const setResultValue = useCallback((id, value) => {
		setResults((prev) =>
			updateObject(prev, {
				[id]: value,
			})
		);
	}, []);

	const handleChangeFactory = useCallback(
		(assignment) => (ev) => {
			if (lockedMap[assignment.id]) return;

			const value = normalizeWhiteSpace(ev.target.value);

			// Only allow inserting spaces (letters must stay the same)
			const baseline = stripWhiteSpace(assignment.text);
			const userLetters = stripWhiteSpace(value);

			if (userLetters !== baseline) return;

			// Limit to 3 whitespace groups (4 words)
			const wsCount = getNumberOfWhiteSpaces(value);
			if (wsCount > WHITE_SPACE_LIMIT) return;

			setResultValue(assignment.id, value);
		},
		[lockedMap, setResultValue]
	);

	return (
		<div className="lit-test lit-word-chain">
			<p>
				<AudioPlayer
					autoplay
					inline
					onPause={() => {
						if (videoRef.current) videoRef.current.play();
					}}
					refs={audioRefs}
					src={introAudio}
				/>
				{strings.ordkjedetestTutorial}
			</p>

			<div className="lit-preview">
				<p className="lit-preview__label">{strings.howTo}</p>
				<video
					className="lit-preview__video"
					onClick={() => videoRef.current && videoRef.current.play()}
					ref={videoRef}
					src={videoSrc}
				/>
			</div>

			<div className="lit-wrapper--thin tutorial">
				<div className="lit-word-chains">
					{assignments.map((assignment, i) => {
						const {audio, answer, count, id, text} = assignment;
						const value = results[id];

						const locked = lockedMap[id];
						const completed =
							locked || getNumberOfWhiteSpaces(value) === WHITE_SPACE_LIMIT;

						const correct = completed && value === answer;

						const showFeedback = completed && !locked;

						const shouldAutoplayFeedback = language === 'se';
						const feedbackSound = correct ? correctAnswerSound : audio;

						return (
							<div
								className={classNames('input-wrapper', {
									'wordchain-locked': locked,
									'wordchain-completed': completed,
									'wordchain-correct': completed && correct,
									'wordchain-wrong': completed && !correct,
								})}
								key={id}
							>
								<div
									className={classNames({
										'lit-input inline': true,
										completed,
									})}
								>
									<label className="lit-input__label tiny">
										{locked ? strings.example ?? 'Exempel' : `${i + 1}`}
									</label>

									<input
										autoFocus={id === firstEditableId}
										className={classNames('lit-input__field', {
											'lit-input__field--locked': locked,
										})}
										onChange={handleChangeFactory(assignment)}
										spellCheck={false}
										autoComplete="off"
										disabled={locked}
										aria-disabled={locked}
										value={value}
									/>
								</div>

								{locked && (
									<p className="comment">
										<span>
											<strong>{strings.solved ?? 'Korrekt:'}</strong>{' '}
											{answer.split(' ').join(', ')}
										</span>
									</p>
								)}

								{showFeedback && (
									<p className="comment">
										{feedbackSound && (
											<AudioPlayer
												autoplay={shouldAutoplayFeedback}
												inline
												refs={audioRefs}
												src={feedbackSound}
												onComplete={() => {
													// For SE: only start the “start test” flow after the last autoplay feedback finishes.
													if (language === 'se' && allRequiredCompleted) {
														setPhase('playStartSound');
													}
												}}
											/>
										)}

										{correct ? (
											<span>{strings.ordkjedeHeltRiktig}</span>
										) : (
											<span>
												{strings.formatString(strings.ordkjedeIkkeRiktig, count)}{' '}
												<strong>{answer.split(' ').join(', ')}</strong>
											</span>
										)}
									</p>
								)}
							</div>
						);
					})}
				</div>

				{/* Only show start sound + button when ALL editable assignments are completed */}
				{allRequiredCompleted  && (
					<p className="instruction">
						<AudioPlayer
							autoplay
							inline
							refs={audioRefs}
							src={startTheTestSound}
							onComplete={() => setPhase('ready')}
						/>
						{strings.ordkjedeStarterTesten}
					</p>
				)}

				{allRequiredCompleted && phase === 'ready' && (
					<button className="lit-btn bg-primary" onClick={() => onNextStep(results)}>
						{strings.start}
					</button>
				)}
			</div>
		</div>
	);
};

export default connect(() => ({}))(Test);
